import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { DollarSign, Mail, Phone, ShieldAlert, Zap, Send, User, Bot, Copy } from "lucide-react";

const SCENARIOS = [
  { key: "initial_offer", label: "Initial offer" },
  { key: "counter_offer", label: "Counter offer" },
  { key: "competing_offer", label: "Competing offer" },
  { key: "raise_request", label: "Ask for a raise" },
  { key: "promotion", label: "Promotion" },
];

export default function Negotiation() {
  const [tab, setTab] = useState("scripts");
  return (
    <div className="p-10 max-w-5xl" data-testid="negotiation-page">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 11</div>
        <h1 className="font-display font-bold text-5xl tracking-tight">Negotiation Coach</h1>
        <p className="text-muted-foreground mt-2 text-sm">Tactical scripts. Live roleplay. Stop leaving $20K on the table.</p>
      </div>
      <div className="inline-flex gap-px bg-border border border-border mb-8">
        <button data-testid="tab-scripts" onClick={()=>setTab("scripts")} className={`px-5 py-2.5 text-sm flex items-center gap-2 ${tab==="scripts"?"bg-primary text-foreground":"bg-card text-muted-foreground hover:text-foreground"}`}><Mail size={14}/> Scripts</button>
        <button data-testid="tab-practice" onClick={()=>setTab("practice")} className={`px-5 py-2.5 text-sm flex items-center gap-2 ${tab==="practice"?"bg-primary text-foreground":"bg-card text-muted-foreground hover:text-foreground"}`}><Bot size={14}/> Live Roleplay</button>
      </div>
      {tab === "scripts" ? <Scripts/> : <Roleplay/>}
    </div>
  );
}

function Scripts() {
  const [scenario, setScenario] = useState("counter_offer");
  const [currentOffer, setCurrentOffer] = useState("");
  const [target, setTarget] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async () => {
    setLoading(true);
    try {
      const body = { scenario, current_offer: currentOffer ? Number(currentOffer) : null, target: target ? Number(target) : null, context };
      const r = await api.post("/negotiation/scripts", body);
      setData(r.data); toast.success("Scripts ready");
    } catch (e) { toast.error("Failed"); } finally { setLoading(false); }
  };

  return (
    <div data-testid="scripts-tab">
      <div className="card-flat p-6 mb-6">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Scenario</label>
            <select data-testid="neg-scenario" value={scenario} onChange={e=>setScenario(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3">
              {SCENARIOS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Current offer (USD)</label>
            <input data-testid="neg-current" type="number" value={currentOffer} onChange={e=>setCurrentOffer(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3"/>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Your target (USD)</label>
            <input data-testid="neg-target" type="number" value={target} onChange={e=>setTarget(e.target.value)} className="w-full mt-2 bg-card border border-border px-4 py-3"/>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Extra context (optional)</label>
            <input data-testid="neg-context" value={context} onChange={e=>setContext(e.target.value)} placeholder="e.g., competing offer of $130K" className="w-full mt-2 bg-card border border-border px-4 py-3"/>
          </div>
        </div>
        <button data-testid="neg-generate" onClick={run} disabled={loading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors mt-4 px-6 py-2.5">{loading? "Generating..." : "Generate playbook"}</button>
      </div>

      {data && (
        <div className="space-y-4" data-testid="scripts-result">
          <div className="card-flat p-5 border-l-2 border-l-[#0F52BA]">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-2">Diagnosis</div>
            <div className="text-sm text-zinc-200">{data.diagnosis}</div>
          </div>
          <div className="card-flat p-5">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-emerald-400 mb-2"><DollarSign size={12}/> Target ask</div>
            <div className="font-display font-bold text-xl">{data.target_ask}</div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <ScriptPanel title="Email script" icon={Mail} text={data.email_script}/>
            <ScriptPanel title="Verbal script" icon={Phone} text={data.verbal_script}/>
          </div>
          <div className="card-flat p-5">
            <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3">Counter moves</div>
            <div className="space-y-3">
              {(data.counter_moves||[]).map((m,i) => (
                <div key={i} className="border-l border-l-[#0F52BA] pl-4">
                  <div className="text-xs text-amber-400 mb-1">Objection: {m.objection}</div>
                  <div className="text-sm text-zinc-200">{m.response}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="Leverage to use" items={data.leverage_to_use} icon={Zap}/>
            <Panel title="Walk-away conditions" items={data.walk_away_conditions} icon={ShieldAlert}/>
          </div>
        </div>
      )}
    </div>
  );
}

function Roleplay() {
  const [scenario, setScenario] = useState("counter_offer");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef();

  useEffect(() => { api.get("/negotiation/practice/history").then(r => setMessages(r.data)).catch(() => {}); }, []);
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim(); setInput("");
    setMessages(prev => [...prev, { role: "user", content: q, id: "u"+Date.now() }]);
    setLoading(true);
    try {
      const { data } = await api.post("/negotiation/practice", { message: q, scenario });
      setMessages(prev => [...prev, { role: "assistant", content: data.reply, id: "a"+Date.now() }]);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col h-[70vh]" data-testid="practice-tab">
      <div className="flex items-center gap-2 mb-4">
        <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Roleplay as:</div>
        <select data-testid="neg-practice-scenario" value={scenario} onChange={e=>setScenario(e.target.value)} className="bg-card border border-border px-3 py-1.5 text-xs">
          {SCENARIOS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <div className="text-xs text-muted-foreground ml-auto">Claude plays the recruiter · You negotiate</div>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-2" data-testid="practice-messages">
        {messages.length === 0 && (
          <div className="border border-border p-6 text-sm text-muted-foreground">Kick it off — send an opener like "Thanks for the offer. Can we discuss the base salary?"</div>
        )}
        {messages.map(m => (
          <div key={m.id} className={`flex gap-3 ${m.role==="user"?"justify-end":""}`}>
            {m.role==="assistant" && <div className="w-8 h-8 bg-secondary border border-border flex items-center justify-center shrink-0"><Bot size={14} className="text-primary"/></div>}
            <div className={`max-w-[75%] p-4 ${m.role==="user"?"bubble-user":"bubble-ai"} text-sm whitespace-pre-wrap`}>{m.content}</div>
            {m.role==="user" && <div className="w-8 h-8 bg-primary flex items-center justify-center shrink-0"><User size={14}/></div>}
          </div>
        ))}
        {loading && <div className="bubble-ai p-4 text-sm text-muted-foreground max-w-[75%]">Thinking...</div>}
      </div>
      <div className="pt-3 flex gap-2">
        <input data-testid="practice-input" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")send();}} placeholder="Your reply to the recruiter..." className="flex-1 bg-card border border-border px-4 py-3"/>
        <button data-testid="practice-send" onClick={send} disabled={loading} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-5"><Send size={16}/></button>
      </div>
    </div>
  );
}

function ScriptPanel({ title, icon: Icon, text }) {
  return (
    <div className="card-flat p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground"><Icon size={12}/> {title}</div>
        <button onClick={()=>{navigator.clipboard.writeText(text); toast.success("Copied");}} className="text-xs text-primary hover:underline flex items-center gap-1"><Copy size={11}/> Copy</button>
      </div>
      <div className="whitespace-pre-wrap text-sm text-zinc-200 leading-relaxed max-h-[400px] overflow-y-auto">{text}</div>
    </div>
  );
}
function Panel({ title, items, icon: Icon }) {
  return (
    <div className="card-flat p-5">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3"><Icon size={12}/> {title}</div>
      <ul className="space-y-2">
        {(items||[]).map((it,i) => <li key={i} className="text-sm text-foreground/80 flex gap-2"><span className="w-1 h-1 bg-primary mt-2 shrink-0"/> {it}</li>)}
      </ul>
    </div>
  );
}
